import { DateTime } from "luxon";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { BeneficiaryDesignationInput } from "../../mutations";
import type { BeneficiaryRecord } from "../../../person/queries";

export const MASKED_SSN_LAST4 = "••••";

/** Alpha-3 code used by CountryDropdown (matches countries.json United States). */
export const DEFAULT_BENEFICIARY_COUNTRY_CODE = "USA";

export const BENEFICIARY_APPLICATION_TYPES = ["member", "associate", "affiliate"] as const;

export type BeneficiaryApplicationType = (typeof BENEFICIARY_APPLICATION_TYPES)[number];

export const isBeneficiaryApplicationType = (value: string): value is BeneficiaryApplicationType =>
  BENEFICIARY_APPLICATION_TYPES.includes(value as BeneficiaryApplicationType);

export type BeneficiaryFormRecord = {
  recordId?: string;
  fullName: string;
  dateOfBirth: string;
  ssnLast4: string;
  relationship: string;
  percent: string;
  applicationType?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  countryCode?: string;
  phone?: string;
  email?: string;
  isVerified?: boolean;
};

export type BeneficiariesFormValues = {
  effectiveStartDate: string;
  primaryBeneficiaries: BeneficiaryFormRecord[];
  contingentBeneficiaries: BeneficiaryFormRecord[];
};

export type BeneficiaryGroup = "primaryBeneficiaries" | "contingentBeneficiaries";

export const RELATIONSHIP_OPTIONS = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Domestic Partner",
  "Trust",
  "Estate",
  "Other"
].map((value) => ({ value, label: value }));

export function defaultEffectiveStartDate(): string {
  return DateTime.now().toFormat("yyyy-MM-dd");
}

export function validateEffectiveStartDate(value: string | undefined): true | string {
  if (!requiredText(value)) return "Effective start date is required";

  const parsed = DateTime.fromISO(value!, { zone: "local" }).startOf("day");
  if (!parsed.isValid) return "Enter a valid date";

  if (parsed > DateTime.now().startOf("day")) return "Date cannot be in the future";

  return true;
}

export function defaultCountryCode(country: string | null | undefined): string {
  const trimmed = (country ?? "").trim();
  return trimmed || DEFAULT_BENEFICIARY_COUNTRY_CODE;
}

export const emptyBeneficiary = (applicationType?: string): BeneficiaryFormRecord => ({
  fullName: "",
  dateOfBirth: "",
  ssnLast4: "",
  relationship: "",
  percent: "",
  applicationType,
  address1: "",
  address2: "",
  city: "",
  state: "",
  postalCode: "",
  countryCode: DEFAULT_BENEFICIARY_COUNTRY_CODE,
  phone: "",
  email: "",
  isVerified: false
});

export function blankBeneficiariesFormValues(applicationType?: string): BeneficiariesFormValues {
  return {
    effectiveStartDate: defaultEffectiveStartDate(),
    primaryBeneficiaries: [emptyBeneficiary(applicationType)],
    contingentBeneficiaries: []
  };
}

export function applicationTypeFromProgramName(
  programTypeName?: string | null
): Extract<BeneficiaryApplicationType, "associate" | "member"> | null {
  const normalized = programTypeName?.trim().toLowerCase();
  if (normalized === "associate" || normalized === "member") return normalized;
  return null;
}

export function updateBeneficiaryApplicationTypes(
  programTypeName: string | null | undefined,
  activeRecords: Array<Pick<BeneficiaryRecord, "applicationType">>
): BeneficiaryApplicationType[] {
  const types = new Set<BeneficiaryApplicationType>();
  const fromProgram = applicationTypeFromProgramName(programTypeName);
  if (fromProgram) types.add(fromProgram);

  for (const record of activeRecords) {
    if (isBeneficiaryApplicationType(record.applicationType)) {
      types.add(record.applicationType);
    }
  }

  return [...types].sort();
}

export function toFormRecord(record: BeneficiaryRecord): BeneficiaryFormRecord {
  return {
    recordId: record.id,
    fullName: record.fullName,
    dateOfBirth: record.dateOfBirth,
    ssnLast4: record.ssnLast4,
    relationship: record.relationship,
    percent: String(record.percent),
    applicationType: record.applicationType,
    address1: record.address1 ?? "",
    address2: record.address2 ?? "",
    city: record.city ?? "",
    state: record.state ?? "",
    postalCode: record.postalCode ?? "",
    countryCode: defaultCountryCode(record.country),
    phone: record.phone ?? "",
    email: record.email ?? ""
  };
}

export function buildDefaultValues(
  records: BeneficiaryRecord[],
  applicationTypeOverride?: string
): BeneficiariesFormValues {
  const applicationType = records[0]?.applicationType ?? applicationTypeOverride ?? "member";
  const primaryBeneficiaries = records.filter((record) => record.beneficiaryType === "primary").map(toFormRecord);
  const contingentBeneficiaries = records.filter((record) => record.beneficiaryType === "contingent").map(toFormRecord);

  return {
    effectiveStartDate: defaultEffectiveStartDate(),
    primaryBeneficiaries: primaryBeneficiaries.length ? primaryBeneficiaries : [emptyBeneficiary(applicationType)],
    contingentBeneficiaries
  };
}

export function sumPercents(beneficiaries: BeneficiaryFormRecord[]): number {
  return beneficiaries.reduce((sum, beneficiary) => sum + Number(beneficiary.percent || 0), 0);
}

export function ssnLast4Validation(value: string, record?: BeneficiaryFormRecord) {
  if (record?.recordId && value === MASKED_SSN_LAST4) return true;
  return /^\d{4}$/.test(value) || "Must be exactly 4 digits";
}

export function requiredText(value: string | undefined): boolean {
  return (value ?? "").trim().length > 0;
}

export function requiredFieldRules(message: string) {
  return {
    required: message,
    validate: (value: string | undefined) => requiredText(value) || message
  };
}

/** Matches backend Phonelib validation: optional, but present values must be valid E.164. */
export function validateBeneficiaryPhone(value: string | undefined): true | string {
  if (!value?.trim()) return true;

  const parsed = parsePhoneNumberFromString(value.trim());
  return parsed?.isValid() ? true : "Enter a valid phone number";
}

function isUsMailingCountry(countryCode?: string): boolean {
  const normalized = (countryCode ?? "").trim().toUpperCase();
  return normalized === "USA" || normalized === "US" || normalized === "UNITED STATES";
}

export function isFilledBeneficiary(record: BeneficiaryFormRecord, contactRequired = false): boolean {
  const percent = Number(record.percent);
  const coreComplete =
    requiredText(record.fullName) &&
    requiredText(record.dateOfBirth) &&
    requiredText(record.relationship) &&
    ssnLast4Validation(record.ssnLast4, record) === true &&
    Number.isInteger(percent) &&
    percent >= 1 &&
    percent <= 100;

  if (!coreComplete) return false;
  if (!contactRequired) return true;

  const stateRequired = isUsMailingCountry(record.countryCode);

  return (
    requiredText(record.address1) &&
    requiredText(record.city) &&
    (!stateRequired || requiredText(record.state)) &&
    requiredText(record.postalCode) &&
    requiredText(record.countryCode)
  );
}

export function beneficiariesGroupCanSave(
  records: BeneficiaryFormRecord[],
  required: boolean,
  contactRequired = false
): boolean {
  if (records.length === 0) return !required;
  return records.every((record) => isFilledBeneficiary(record, contactRequired)) && sumPercents(records) === 100;
}

export function beneficiariesFormCanSave(values: BeneficiariesFormValues, contactRequired = false): boolean {
  return (
    validateEffectiveStartDate(values.effectiveStartDate) === true &&
    beneficiariesGroupCanSave(values.primaryBeneficiaries, true, contactRequired) &&
    beneficiariesGroupCanSave(values.contingentBeneficiaries, false, contactRequired)
  );
}

export function applicationTypeLabel(applicationType: string): string {
  return applicationType.charAt(0).toUpperCase() + applicationType.slice(1);
}

export function toMutationInput(
  beneficiary: BeneficiaryFormRecord,
  includeContact = false
): BeneficiaryDesignationInput {
  return {
    ...(beneficiary.recordId ? { id: beneficiary.recordId } : {}),
    fullName: beneficiary.fullName.trim(),
    dateOfBirth: beneficiary.dateOfBirth,
    ...(beneficiary.ssnLast4 && beneficiary.ssnLast4 !== MASKED_SSN_LAST4 ? { ssnLast4: beneficiary.ssnLast4 } : {}),
    relationship: beneficiary.relationship,
    percent: Number(beneficiary.percent),
    applicationType: beneficiary.applicationType,
    ...(includeContact
      ? {
          address1: beneficiary.address1?.trim() || null,
          address2: beneficiary.address2?.trim() || null,
          city: beneficiary.city?.trim() || null,
          state: beneficiary.state?.trim() || null,
          postalCode: beneficiary.postalCode?.trim() || null,
          country: beneficiary.countryCode?.trim() || null,
          phone: beneficiary.phone?.trim() || null,
          email: beneficiary.email?.trim() || null
        }
      : {})
  };
}

export function allocationSummaryHeadline(allocationSum: number, allocationValid: boolean): string {
  if (allocationValid) {
    return `Allocation currently ${allocationSum}%.`;
  }
  if (allocationSum > 100) {
    return `Allocation currently ${allocationSum}%. Total exceeds 100% by ${allocationSum - 100}%.`;
  }
  return `Allocation currently ${allocationSum}%. You have ${100 - allocationSum}% left to distribute.`;
}
